// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import React, { useCallback, useEffect, useRef } from 'react';
import clsx from 'clsx';
import { useUniqueId } from '@cloudscape-design/component-toolkit/internal';
import { getBaseProps } from '../internal/base-component';
import { screenReaderTextClass } from '../internal/components/chart-series-details/series-details-text';
import { Transition } from '../internal/components/transition';
import { fireNonCancelableEvent } from '../internal/events';
import { useControllable } from '../internal/hooks/use-controllable';
import { useOneTheme } from '../internal/hooks/use-visual-mode';
import { KeyCode } from '../internal/keycode';
import { ExpandableSectionContainer } from './expandable-section-container';
import { ExpandableSectionHeader } from './expandable-section-header';
import { variantSupportsDescription } from './utils';
import analyticsSelectors from './analytics-metadata/styles.css.js';
import styles from './styles.css.js';
export default function InternalExpandableSection({ expanded: controlledExpanded, defaultExpanded, onChange, variant = 'default', children, header, headerText, headerCounter, headerDescription, headerInfo, headerActions, headingTagOverride, disableContentPaddings, headerAriaLabel, __internalRootRef, __injectAnalyticsComponentMetadata, __expandIconPosition = 'start', __disableHeaderPaddings = false, __hideExpandIcon = false, ...props }) {
    const contentInnerRef = useRef(null);
    const isOneTheme = useOneTheme();
    const controlId = useUniqueId();
    const triggerControlId = `${controlId}-trigger`;
    const descriptionId = `${controlId}-description`;
    const baseProps = getBaseProps(props);
    const [expanded, setExpanded] = useControllable(controlledExpanded, onChange, defaultExpanded, {
        componentName: 'ExpandableSection',
        controlledProp: 'expanded',
        changeHandler: 'onChange',
    });
    const onExpandChange = useCallback((newExpanded) => {
        setExpanded(newExpanded);
        fireNonCancelableEvent(onChange, { expanded: newExpanded });
    }, [onChange, setExpanded]);
    const onClick = useCallback(() => {
        onExpandChange(!expanded);
    }, [onExpandChange, expanded]);
    const onKeyUp = useCallback((event) => {
        const interactionKeys = [KeyCode.enter, KeyCode.space];
        if (interactionKeys.indexOf(event.keyCode) !== -1) {
            onExpandChange(!expanded);
        }
    }, [onExpandChange, expanded]);
    const onKeyDown = useCallback((event) => {
        if (event.keyCode === KeyCode.space) {
            // Prevent the page from scrolling when toggling the component with the space bar.
            event.preventDefault();
        }
    }, []);
    // Set inert on collapsed content to block pointer/focus events and hide from
    // assistive technology. Direct DOM manipulation needed until React supports
    // the inert attribute natively. https://github.com/facebook/react/issues/17157
    useEffect(() => {
        if (contentInnerRef.current) {
            contentInnerRef.current.inert = !expanded;
        }
    }, [expanded]);
    const triggerProps = {
        ariaControls: controlId,
        ariaLabel: headerAriaLabel,
        ariaLabelledBy: headerAriaLabel ? undefined : triggerControlId,
        onKeyUp,
        onKeyDown,
        onClick,
    };
    // Map stacked variant to container to avoid code duplication
    const baseVariant = variant === 'stacked' ? 'container' : variant;
    return (React.createElement(ExpandableSectionContainer, { ...props, expanded: expanded, className: clsx(baseProps.className, styles.root, analyticsSelectors.root), variant: variant, __injectAnalyticsComponentMetadata: __injectAnalyticsComponentMetadata, header: React.createElement(ExpandableSectionHeader, { id: triggerControlId, descriptionId: descriptionId, className: clsx(styles.header, styles[`header-${baseVariant}`], screenReaderTextClass, __disableHeaderPaddings && styles['disable-header-paddings']), variant: baseVariant, expanded: !!expanded, header: header, headerText: headerText, headerDescription: headerDescription, headerCounter: headerCounter, headerInfo: headerInfo, headerActions: headerActions, headingTagOverride: headingTagOverride, expandIconPosition: __expandIconPosition, hideExpandIcon: __hideExpandIcon, ...triggerProps }), __internalRootRef: __internalRootRef },
        React.createElement(Transition, { in: !!expanded, disabled: !isOneTheme }, (transitionState, transitionEventsRef, motionDisabled) => {
            const contentSettled = !!expanded && (motionDisabled || transitionState === 'entered');
            return (React.createElement("div", { ref: transitionEventsRef, id: controlId, className: clsx(styles.content, styles[`content-${baseVariant}`], expanded && styles['content-expanded'], disableContentPaddings && styles['disable-content-paddings']), role: "group", "aria-label": triggerProps.ariaLabel, "aria-labelledby": triggerProps.ariaLabelledBy, "aria-describedby": variantSupportsDescription(baseVariant) && headerDescription ? descriptionId : undefined },
                React.createElement("div", { ref: contentInnerRef, className: clsx(styles['content-inner'], expanded && styles['content-inner-expanded'], contentSettled && styles['content-inner-settled']) },
                    React.createElement("div", { className: clsx(styles['content-inner-body'], styles[`content-inner-body-${baseVariant}`], disableContentPaddings && styles['disable-content-paddings']) }, children))));
        })));
}
//# sourceMappingURL=internal.js.map